import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SMART_ROUTES } from '../../constants';
import {
  smartMockCompose,
  smartMockDestinations,
  smartMockFundingMethods,
  smartMockOrigins,
  smartMockPayoutHints,
  smartMockPayoutMethods,
  smartMockQuote,
} from '../../gallery/smartMock';
import { Accordion, AccordionStep } from './Accordion';
import { RecommendedCard } from './RecommendedCard';
import { FlagIcon } from './ui/FlagIcon';
import { FundingStep } from './steps/FundingStep';
import { IntentStep } from './steps/IntentStep';
import { PayoutStep } from './steps/PayoutStep';
import { RouteAmountStep } from './steps/RouteAmountStep';
import { formatCurrency } from './utils/format';
import type { ComposeStep, Intent } from './wizard/types';
import { composeStepsFor } from './wizard/types';

export type WizardComposeFocus = ComposeStep;

interface WizardComposePanelProps {
  /** Which accordion step is open when the gallery screen mounts. */
  focusStep: WizardComposeFocus;
}

/**
 * Gallery-safe compose accordion. Seeded open step + local state only —
 * no XState / catalog / pre-quote network.
 */
export const WizardComposePanel = ({ focusStep }: WizardComposePanelProps) => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();

  const [intent, setIntent] = useState<Intent | null>(
    focusStep === 'intent' ? null : smartMockCompose.intent,
  );
  const [route, setRoute] = useState(smartMockCompose.route);
  const [funding, setFunding] = useState(smartMockCompose.funding);
  const [payout, setPayout] = useState(smartMockCompose.payout);
  const [openStep, setOpenStep] = useState<ComposeStep | null>(focusStep);

  const activeSteps = composeStepsFor(intent);
  const showFunding = activeSteps.includes('funding');
  const showPayout = activeSteps.includes('payout');

  const fromCode = route.fromCountry ?? 'US';
  const toCode = route.toCountry ?? 'MX';
  const amountNum = route.amount ? parseFloat(route.amount) : 0;

  const stepCompleted: Record<ComposeStep, boolean> = {
    intent: intent !== null,
    route: Boolean(
      route.fromCountry &&
        route.toCountry &&
        route.amount &&
        parseFloat(route.amount) > 0,
    ),
    funding: funding !== null,
    payout: payout !== null,
  };

  const ready =
    stepCompleted.intent &&
    stepCompleted.route &&
    (!showFunding || stepCompleted.funding) &&
    (!showPayout || stepCompleted.payout);

  const indexOf = (step: ComposeStep): number => activeSteps.indexOf(step);

  const stepTitleFor = (step: ComposeStep): string => {
    switch (step) {
      case 'intent':
        return t('wizard.compose.stepTitle.intent');
      case 'route':
        return t(
          intent === 'request'
            ? 'wizard.compose.stepTitle.route.request'
            : 'wizard.compose.stepTitle.route.default',
        );
      case 'funding':
        return t('wizard.compose.stepTitle.funding');
      case 'payout':
        return t(
          intent === 'request'
            ? 'wizard.compose.stepTitle.payout.request'
            : 'wizard.compose.stepTitle.payout.default',
        );
    }
  };

  const intentLabel = intent ? t(`wizard.intent.${intent}`) : '';
  const fundingSummary = funding
    ? t(`methods.funding.${funding}.displayName`)
    : '';
  const payoutSummary = payout
    ? t(`methods.payout.${payout}.displayName`)
    : '';

  const quote = amountNum > 0 ? smartMockQuote : null;

  return (
    <div className="flex flex-col gap-5">
      <Accordion>
        <AccordionStep
          index={indexOf('intent')}
          open={openStep === 'intent'}
          completed={stepCompleted.intent}
          title={stepTitleFor('intent')}
          summary={intentLabel}
          onOpen={() => setOpenStep('intent')}
        >
          <IntentStep
            selected={intent}
            onSelect={(next) => {
              setIntent(next);
              setOpenStep('route');
            }}
          />
        </AccordionStep>

        <AccordionStep
          index={indexOf('route')}
          open={openStep === 'route'}
          completed={stepCompleted.route}
          disabled={!stepCompleted.intent && focusStep !== 'route'}
          title={stepTitleFor('route')}
          summary={
            stepCompleted.route ? (
              <span className="inline-flex items-center gap-2 text-base">
                <FlagIcon code={fromCode} />
                <span aria-hidden>→</span>
                <FlagIcon code={toCode} />
                <span className="ml-1 text-sm font-medium">
                  {formatCurrency(amountNum, 'MXN')}
                </span>
              </span>
            ) : (
              ''
            )
          }
          onOpen={() => setOpenStep('route')}
        >
          <RouteAmountStep
            origins={smartMockOrigins}
            destinations={smartMockDestinations}
            route={route}
            onChange={(patch) => setRoute((r) => ({ ...r, ...patch }))}
            onAdvance={() => {
              if (!stepCompleted.route) return;
              setOpenStep(showFunding ? 'funding' : showPayout ? 'payout' : null);
            }}
          />
        </AccordionStep>

        {showFunding && (
          <AccordionStep
            index={indexOf('funding')}
            open={openStep === 'funding'}
            completed={stepCompleted.funding}
            disabled={!stepCompleted.route && focusStep !== 'funding'}
            title={stepTitleFor('funding')}
            summary={fundingSummary}
            onOpen={() => setOpenStep('funding')}
          >
            <FundingStep
              available={smartMockFundingMethods}
              selected={funding}
              onSelect={(m) => {
                setFunding(m);
                setOpenStep(showPayout ? 'payout' : null);
              }}
            />
          </AccordionStep>
        )}

        {showPayout && (
          <AccordionStep
            index={indexOf('payout')}
            open={openStep === 'payout'}
            completed={stepCompleted.payout}
            disabled={
              (!stepCompleted.funding && showFunding && focusStep !== 'payout') ||
              (!stepCompleted.route && !showFunding && focusStep !== 'payout')
            }
            title={stepTitleFor('payout')}
            summary={payoutSummary}
            onOpen={() => setOpenStep('payout')}
          >
            <PayoutStep
              available={smartMockPayoutMethods}
              selected={payout}
              hints={smartMockPayoutHints}
              onSelect={(m) => {
                setPayout(m);
                setOpenStep(null);
              }}
            />
          </AccordionStep>
        )}
      </Accordion>

      <RecommendedCard
        quote={quote}
        ready={ready}
        loading={false}
        bullets={[
          t('wizard.compose.bullets.multipleFunding'),
          t('wizard.compose.bullets.cashPickup'),
        ]}
        brandName="Lola Send"
        intent={intent}
        onContinue={() => navigate(SMART_ROUTES.COLLECT_RECIPIENT)}
      />
    </div>
  );
};
